import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  Tooltip,
  Box,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import AuthContext from "../Context/AuthProvider";
import { useSnackbar } from "../Utility/UserMessageContext";
import {
  addEnquiryComment,
  getEnquiryComments,
  deleteDocumentNote,
  updateDocumentNote,
} from "../API/EnquiryCommentApi";
import { convertUTCToTimeZoneDateTimeString } from "../Utility/DateTimeUtility";
import {
  getEnquiriesById,
  updateEnquiryComment,
  deleteEnquiryComment,
} from "../API/EnquiryApi";
import { showLoadingImage } from "../Utility/Utility";

const EnquiryComments = ({ enquiryId, enquiryComments }) => {
  const [newComment, setNewComment] = useState("");
  const [existingComments, setExistingComments] = useState(enquiryComments);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const { showSnackbar } = useSnackbar();
  const { auth } = useContext(AuthContext);
  const [isSaving, setIsSaving] = useState(false);

  // const documentType = "Enquiry";

  useEffect(() => {
    refreshEnquiries();
  }, [enquiryId, auth]);

  const refreshEnquiries = async () => {
    if (enquiryId) {
      const result = await getEnquiriesById(auth, enquiryId);
      if (result?.isSuccess) {
        const sortedNotes = result?.data?.comments?.sort(
          (a, b) => new Date(b.lastModifiedOn) - new Date(a.lastModifiedOn)
        );
        setExistingComments(sortedNotes);
      } else {
        showSnackbar("Failed to load notes: " + result.data, "error");
      }
    }
  };

  const handleAddComment = async () => {
    setIsSaving(true);
    if (!newComment.trim()) return;

    const commentToBeAdded = {
      comment: newComment,
      enquiryId: enquiryId,
    };

    const result = await addEnquiryComment(auth, commentToBeAdded);
    if (result?.isSuccess) {
      await refreshEnquiries();
      setNewComment("");
      showSnackbar("Note added successfully", "success");
    } else {
      showSnackbar("Failed to add note: " + result.data, "error");
    }
    setIsSaving(false);
  };

  const handleDelete = async (id) => {
    setIsSaving(true);
    const result = await deleteEnquiryComment(auth, id);
    if (result?.isSuccess) {
      showSnackbar("Note deleted successfully", "success");
      await refreshEnquiries();
    } else {
      showSnackbar("Failed to delete note: " + result.data, "error");
    }
    setIsSaving(false);
  };

  const handleEdit = (id, text) => {
    setEditId(id);
    setEditText(text);
  };

  const handleSaveEdit = async (id) => {
    setIsSaving(true);
    const result = await updateEnquiryComment(auth, { id, comment: editText });
    if (result?.isSuccess) {
      await refreshEnquiries();
      setEditId(null);
    } else {
      showSnackbar("Failed to update note: " + result.data, "error");
    }
    setIsSaving(false);
  };

  const handleCancelEdit = () => {
    setEditId(null);
    setEditText("");
  };

  return (
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        p: 2,
        background: "#f5f7fa",
        borderRadius: 3,
        boxShadow: 1,
        maxHeight: "80vh",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {isSaving && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(216, 214, 214, 0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1301,
          }}
        >
          {showLoadingImage()}
        </div>
      )}
      <CardContent
        sx={{ flex: 1, display: "flex", flexDirection: "column", p: 0 }}
      >
        {/* Add Note */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Add Enquiry Follow-up Note
        </Typography>
        <TextField
          label="Enter follow-up note"
          multiline
          minRows={3}
          maxRows={6}
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          fullWidth
          variant="outlined"
          sx={{ mb: 2, background: "#fff", borderRadius: 2 }}
        />
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddComment}
            disabled={!newComment.trim()}
            sx={{ textTransform: "none" }}
          >
            Add Note
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* List of Notes */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          Enquiry Follow-up Notes
        </Typography>
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          sx={{ overflowY: "auto", flex: 1 }}
        >
          {existingComments?.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No follow-up notes yet.
            </Typography>
          )}
          {existingComments?.map((note) => (
            <Card
              key={note.id}
              variant="outlined"
              sx={{ background: "#f9f9f9", borderRadius: 2 }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    {convertUTCToTimeZoneDateTimeString(
                      note.createdOn,
                      auth?.selectedFacility?.timeZone
                    )}
                  </Typography>
                  <Box>
                    {editId === note.id ? (
                      <>
                        <Tooltip title="Save">
                          <IconButton
                            color="primary"
                            onClick={() => handleSaveEdit(note.id)}
                            size="small"
                          >
                            <SaveIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton
                            color="secondary"
                            onClick={handleCancelEdit}
                            size="small"
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Tooltip title="Edit">
                          <IconButton
                            color="primary"
                            onClick={() => handleEdit(note.id, note.comment)}
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(note.id)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </Box>
                <Divider sx={{ my: 1 }} />
                {editId === note.id ? (
                  <TextField
                    multiline
                    minRows={2}
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    fullWidth
                    variant="outlined"
                    sx={{ mb: 1, background: "#fff", borderRadius: 1 }}
                  />
                ) : (
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {note.comment}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  By: {note.createdByUserId}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default EnquiryComments;
